import {lazy,Suspense,useEffect,useRef,useState,type MouseEvent} from 'react';
import {Link,Navigate,Route,Routes,useLocation,useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {Login} from '../features/administration/Login';
import {TenantFinder} from '../features/administration/TenantFinder';
import {routeAllowed,visibleMenu} from './nav';
import {chooseBranch,rememberBranch} from './branch-memory';
import {useCapabilities} from './capabilities';
import {ACTOR_MISMATCH_EVENT,api,ApiError,bumpSessionEpoch,markActorChanged,setApiActor,setApiBranch} from '../services/api';
import {classifySessionSignal,publishSession,subscribeSession} from './session-sync';
import {classifyRecheckFailure,SESSION_NOTICE_TITLE,sessionNoticeAction,sessionNoticeLines} from './session-notice';
import type {Identity} from '../types';
import {handleFormKey} from './function-keys';
import {ShortcutStrip} from '../components/ShortcutStrip';

type HostContext={
 mode:'main'|'tenant'|'development'|'internal'|'unknown';
 baseDomain:string|null;
 tenant?:{slug:string;name:string};
};

const DashboardPage=lazy(()=>import('../features/dashboard/DashboardPage'));
const AccountsPage=lazy(()=>import('../features/accounts/AccountsPage'));
const AccountingDefaults=lazy(()=>import('../features/accounts/AccountingDefaults'));
const VouchersPage=lazy(()=>import('../features/accounts/VouchersPage'));
const FinancialReports=lazy(()=>import('../features/accounts/FinancialReports'));
const YarnSpecsPage=lazy(()=>import('../features/grey/YarnSpecsPage'));
const QualityFormulaPage=lazy(()=>import('../features/grey/QualityFormulaPage'));
const StockLocationsPage=lazy(()=>import('../features/grey/StockLocationsPage'));
const ContractsPage=lazy(()=>import('../features/grey/ContractsPage'));
const BagPurchasePage=lazy(()=>import('../features/grey/BagPurchasePage'));
const MillIssuePage=lazy(()=>import('../features/grey/MillIssuePage'));
const MillYarnReturnPage=lazy(()=>import('../features/grey/MillYarnReturnPage'));
const FabricReceiptPage=lazy(()=>import('../features/grey/FabricReceiptPage'));
const MillReconciliationPage=lazy(()=>import('../features/grey/MillReconciliationPage'));
const OpenBagPage=lazy(()=>import('../features/grey/OpenBagPage'));
const FinishedFabricStockPage=lazy(()=>import('../features/grey/FinishedFabricStockPage'));
const ContractPositionPage=lazy(()=>import('../features/grey/ContractPositionPage'));
const FabricCostAnalysisPage=lazy(()=>import('../features/grey/FabricCostAnalysisPage'));
const SaleInvoicePage=lazy(()=>import('../features/grey/SaleInvoicePage'));
const MillBillPage=lazy(()=>import('../features/grey/MillBillPage'));
const SalesRegisterPage=lazy(()=>import('../features/grey/SalesRegisterPage'));
const MillChargesReviewPage=lazy(()=>import('../features/grey/MillChargesReviewPage'));
const MillPaymentPage=lazy(()=>import('../features/grey/MillPaymentPage'));
const MillStatementPage=lazy(()=>import('../features/grey/MillStatementPage'));
const PostingReadinessPage=lazy(()=>import('../features/accounts/PostingReadinessPage'));
const ComingSoon=lazy(()=>import('../features/administration/ComingSoon'));
const BranchesPage=lazy(()=>import('../features/administration/BranchesPage'));
const UsersPage=lazy(()=>import('../features/administration/UsersPage'));
const RolesPage=lazy(()=>import('../features/administration/RolesPage'));
const AuthorizationPage=lazy(()=>import('../features/administration/AuthorizationPage'));
const SettingsPage=lazy(()=>import('../features/administration/SettingsPage'));
const CompaniesPage=lazy(()=>import('../features/administration/CompaniesPage'));
const FiscalYearsPage=lazy(()=>import('../features/administration/FiscalYearsPage'));
const SimpleModePage=lazy(()=>import('../features/administration/SimpleModePage'));
const MillBagsBalancePage=lazy(()=>import('../features/grey/MillBagsBalancePage'));
const OpeningBalancesPage=lazy(()=>import('../features/accounts/OpeningBalancesPage'));
const OpeningStockAuthorizationPage=lazy(()=>import('../features/accounts/OpeningStockAuthorizationPage'));
const FinancialActivationPage=lazy(()=>import('../features/administration/FinancialActivationPage'));
const OutstandingReportPage=lazy(()=>import('../features/grey/OutstandingReportPage'));
const InventoryValuationPage=lazy(()=>import('../features/grey/InventoryValuationPage'));
const ProductionYieldPage=lazy(()=>import('../features/grey/ProductionYieldPage'));
const FabricReceiptReportPage=lazy(()=>import('../features/grey/FabricReceiptReportPage'));
const TaxSetupPage=lazy(()=>import('../features/accounts/TaxSetupPage'));
const TaxRegisterPage=lazy(()=>import('../features/accounts/TaxRegisterPage'));
const PartyAdvancesPage=lazy(()=>import('../features/accounts/PartyAdvancesPage'));
const PurchaseReturnPage=lazy(()=>import('../features/grey/PurchaseReturnPage'));
const CustomerContractsPage=lazy(()=>import('../features/grey/CustomerContractsPage'));
const CustomerContractStatusPage=lazy(()=>import('../features/grey/CustomerContractStatusPage'));

function groupForPath(path:string){
 if(path.startsWith('/dashboard'))return 'Overview';
 if(path.startsWith('/system/companies')||path.startsWith('/system/plans'))return 'Platform Admin';
 if(path.startsWith('/system/'))return 'Administration';
 if(path.startsWith('/grey/contracts')||path.startsWith('/grey/customer-contracts')||path.startsWith('/grey/bag-purchases')||path.startsWith('/grey/mill-issues')||path.startsWith('/grey/mill-yarn-returns')||path.startsWith('/grey/opened-bags')||path.startsWith('/grey/fabric-receipts')||path.startsWith('/grey/fabric-sale-drafts')||path.startsWith('/grey/mill-bills')||path.startsWith('/grey/mill-charges-review')||path.startsWith('/grey/mill-payment-requests')||path.startsWith('/grey/mill-reconciliations'))return 'Transactions';
 if(path.startsWith('/grey/finished-fabric-stock')||path.startsWith('/grey/contract-position')||path.startsWith('/grey/fabric-cost-analyses')||path.startsWith('/grey/mill-statements')||path.startsWith('/grey/reports/'))return 'Reports';
 if(path.startsWith('/grey/'))return 'Masters';
 if(path.startsWith('/accounts/all-reports')||path.startsWith('/accounts/reports'))return 'Financial Reports';
 return 'Accounts';
}

function crumb(path:string){
 if(path.startsWith('/dashboard'))return {section:'Home',page:'Dashboard'};
 if(path.startsWith('/grey/yarn-specifications'))return {section:'Grey Conversion',page:'Yarn Specifications'};
 if(path.startsWith('/grey/quality-formula'))return {section:'Grey Conversion',page:'Quality & Formula'};
 if(path.startsWith('/grey/stock-locations'))return {section:'Grey Conversion',page:'Stock Locations'};
 if(path.startsWith('/grey/contracts'))return {section:'Grey Conversion',page:'Transactions > Contract'};
 if(path.startsWith('/grey/bag-purchases'))return {section:'Grey Conversion',page:'Transactions > Bag Purchase'};
 if(path.startsWith('/grey/mill-issues'))return {section:'Grey Conversion',page:'Transactions > Mill Issue'};
 if(path.startsWith('/grey/mill-yarn-returns'))return {section:'Grey Conversion',page:'Transactions > Mill Yarn Return'};
 if(path.startsWith('/grey/opened-bags'))return {section:'Grey Conversion',page:'Open Bag'};
 if(path.startsWith('/grey/fabric-receipts'))return {section:'Grey Conversion',page:'Transactions > Fabric Receipt'};
 if(path.startsWith('/grey/fabric-sale-drafts'))return {section:'Grey Conversion',page:'Transactions > Finished Sale'};
 if(path.startsWith('/grey/customer-contracts'))return {section:'Grey Conversion',page:'Transactions > Customer Contract'};
 if(path.startsWith('/grey/sales-register'))return {section:'Grey Conversion',page:'Sales Register'};
 if(path.startsWith('/grey/mill-bills'))return {section:'Grey Conversion',page:'Mill Bill Draft'};
 if(path.startsWith('/grey/mill-charges-review'))return {section:'Grey Conversion',page:'Mill Charges Review'};
 if(path.startsWith('/grey/mill-payment-requests'))return {section:'Grey Conversion',page:'Mill Payment Request'};
 if(path.startsWith('/grey/mill-reconciliations'))return {section:'Grey Conversion',page:'Mill Reconciliation'};
 if(path.startsWith('/grey/contract-position'))return {section:'Grey Conversion',page:'Contract Position'};
 if(path.startsWith('/grey/finished-fabric-stock'))return {section:'Grey Conversion',page:'Finished Fabric Stock'};
 if(path.startsWith('/grey/fabric-cost-analyses'))return {section:'Grey Conversion',page:'Fabric Cost Analysis'};
 if(path.startsWith('/grey/mill-statements'))return {section:'Grey Conversion',page:'Mill Statement'};
 if(path.startsWith('/grey/accounting-defaults'))return {section:'Grey Conversion',page:'Accounting Defaults'};
 if(path.startsWith('/grey/reports/mill-bags-balance'))return {section:'Grey Conversion',page:'Reports > Mill Bags Balance'};
 if(path.startsWith('/grey/reports/fabric-receipt'))return {section:'Grey Conversion',page:'Reports > Fabric Receipt Report'};
 if(path.startsWith('/accounts/opening-balances'))return {section:'Accounts',page:'Opening Balances'};
 if(path.startsWith('/grey/reports/outstanding'))return {section:'Grey Conversion',page:'Reports > Party Outstanding'};
 if(path.startsWith('/grey/reports/inventory-valuation'))return {section:'Grey Conversion',page:'Reports > Inventory Valuation'};
 if(path.startsWith('/grey/reports/production-yield'))return {section:'Grey Conversion',page:'Reports > Production Yield'};
 if(path.startsWith('/grey/reports/customer-contracts'))return {section:'Grey Conversion',page:'Reports > Customer Contract Status'};
 if(path.startsWith('/system/companies'))return {section:'System',page:'Platform Admin > Companies & Plans'};
 if(path.startsWith('/system/plans'))return {section:'System',page:'Platform Admin > Plans & Billing'};
 if(path.startsWith('/accounts/opening-stock-authorization'))return {section:'Accounts',page:'Opening Fabric Stock Authorization'};
 if(path.startsWith('/system/financial-activation'))return {section:'System',page:'Administration > Financial Activation'};
 if(path.startsWith('/system/branches'))return {section:'System',page:'Administration > Branches'};
 if(path.startsWith('/system/users'))return {section:'System',page:'Administration > Users & Roles'};
 if(path.startsWith('/system/roles'))return {section:'System',page:'Administration > Roles'};
 if(path.startsWith('/system/authorization'))return {section:'System',page:'Administration > Authorization'};
 if(path.startsWith('/system/settings'))return {section:'System',page:'Administration > Admin Settings'};
 if(path.startsWith('/system/fiscal-years'))return {section:'System',page:'Administration > Fiscal Year'};
 if(path.startsWith('/system/simple-mode'))return {section:'System',page:'Administration > Simple mode'};
 if(path.startsWith('/accounts/all-reports'))return {section:'Financial Reports',page:'All Reports'};
 if(path.startsWith('/accounts/vouchers'))return {section:'Accounts',page:'Cash & Bank Voucher'};
 if(path.startsWith('/accounts/reports/posting-readiness'))return {section:'Accounts',page:'Posting Readiness'};
 if(path.startsWith('/accounts/reports'))return {section:'Financial Reports',page:'Report'};
 return {section:'Accounts',page:'Chart of Accounts'};
}

export function App(){
 const [user,setUser]=useState<Identity|null>(null);
 // Doc 40 §4.2: financial buttons follow the server's answer for each action (GET /auth/capabilities).
 const {cap}=useCapabilities(user?`${user.tenantId}|${user.id}|${user.authorizationModel||'LEGACY'}`:null);
 const [host,setHost]=useState<HostContext|null>(null);
 const [loading,setLoading]=useState(true);
 const [sessionChecked,setSessionChecked]=useState(false);
 const [branch,setBranch]=useState('');
 const [filter,setFilter]=useState('');
 const [open,setOpen]=useState('Accounts');
 // Narrow screens: the sidebar is an off-canvas menu opened from the header.
 const [navOpen,setNavOpen]=useState(false);
 // Another tab signed in as someone else: this tab's screens are discarded until the user continues.
 const [sessionNotice,setSessionNotice]=useState(false);
 // The server refused a request with ACTOR_MISMATCH (so that request was NOT saved) / /auth/me could not be reached.
 const [noticeRejected,setNoticeRejected]=useState(false);
 const [noticeUnverified,setNoticeUnverified]=useState(false);
 const noticeRef=useRef(false);
 const [continuing,setContinuing]=useState(false);
 const navigate=useNavigate();
 const location=useLocation();
 const cache=useQueryClient();

 // Oracle Forms-style function keys on every screen: F-key → the enabled toolbar action with that key cap.
 useEffect(()=>{
  const onKey=(event:KeyboardEvent)=>{handleFormKey(event,document.getElementById('workspace'))};
  window.addEventListener('keydown',onKey);
  return ()=>window.removeEventListener('keydown',onKey);
 },[]);

 // Mirrors of the session for callbacks registered once (session re-check), which would otherwise see stale state.
 const sessionRef=useRef<{userKey:string;branch:string}>({userKey:'',branch:''});
 const applyBranch=(id:string)=>{sessionRef.current.branch=id;setApiBranch(id);setBranch(id)};
 const hideNotice=()=>{noticeRef.current=false;setSessionNotice(false);setNoticeRejected(false);setNoticeUnverified(false)};
 const clearSession=()=>{bumpSessionEpoch();setApiActor('');cache.clear();sessionRef.current={userKey:'',branch:''};setApiBranch('');setBranch('');setUser(null);hideNotice()};
 // Trip the request guard first (synchronously), then stop in-flight reads and replace the screens with the notice.
 const actorSwitched=()=>{markActorChanged();void cache.cancelQueries();noticeRef.current=true;setSessionNotice(true)};
 // /auth/me failed: only a confirmed 401 means "signed out"; anything else keeps the notice with a Retry.
 const recheckFailed=(error:unknown)=>{
  if(classifyRecheckFailure(error)==='signed-out'){clearSession();return}
  if(noticeRef.current)setNoticeUnverified(true);
 };
 const accept=(u:Identity,adopt=false)=>{
  const userKey=`${u.tenantId}|${u.id}`;
  // A different actor showing up on a re-check is never adopted silently: unsaved work belongs to the old one.
  if(sessionRef.current.userKey&&sessionRef.current.userKey!==userKey&&!adopt){actorSwitched();return}
  bumpSessionEpoch();
  const same=sessionRef.current.userKey===userKey;
  // A re-check of the same session keeps the working branch while it is still granted, so a form being
  // edited never switches branch underneath the user; a new login or a revoked branch picks a valid one.
  const previous=same?sessionRef.current.branch:'';
  const id=chooseBranch(u,previous);
  if(!same||id!==previous)cache.clear();
  sessionRef.current.userKey=userKey;
  applyBranch(id);
  rememberBranch(u,id);
  setApiActor(`${u.tenantId}:${u.id}`);
  hideNotice();
  setUser(u);
 };
 // Tell the other tabs FIRST, before any local post-login work, to keep the window in which another tab still
 // sends with its old identity as short as possible. This does not close that window (the cookie changed when
 // the login response arrived); the server-side X-Esaas-Actor check is what rejects such a request.
 const loggedIn=(u:Identity)=>{publishSession('login',{tenantId:u.tenantId,userId:u.id});accept(u)};
 const signOut=async()=>{
  const actor=user?{tenantId:user.tenantId,userId:user.id}:null;
  try{await api('/auth/logout',{method:'POST'})}catch{/* session may already be gone */}
  publishSession('logout',actor);
  clearSession();
 };
 const continueAsCurrent=()=>{
  setContinuing(true);
  setNoticeUnverified(false);
  void api<Identity>('/auth/me').then(u=>accept(u,true)).catch(recheckFailed).finally(()=>setContinuing(false));
 };
 const guardNavigate=(event:MouseEvent,to:string)=>{
  if(to===location.pathname){event.preventDefault();return}
  const leave=new Event('esaas-before-navigate',{cancelable:true});
  window.dispatchEvent(leave);
  if(leave.defaultPrevented)event.preventDefault();
 };

 useEffect(()=>{
  // Do not gate setState on a cancelled flag: React Strict Mode remounts would
  // otherwise drop a successful /auth/me and leave the UI on Login forever.
  void (async()=>{
   try{setHost(await api<HostContext>('/host-context'))}
   catch{setHost({mode:'development',baseDomain:null})}
   try{accept(await api<Identity>('/auth/me'))}
   catch{/* signed out */}
   setSessionChecked(true);
   setLoading(false);
  })();
 },[]);
 useEffect(()=>{
  const expired=()=>{
   // Confirm with /auth/me before wiping UI — avoids false clears from transient API 401s on remount.
   void api<Identity>('/auth/me').then(u=>accept(u)).catch(recheckFailed);
  };
  window.addEventListener('esaas-session-expired',expired);
  return()=>window.removeEventListener('esaas-session-expired',expired);
 },[cache]);
 useEffect(()=>{
  // The server refused a request because the session now belongs to another actor: block the screens, record
  // that the request was NOT saved, and find out who is signed in now (a 401 means nobody → Login).
  const mismatch=()=>{
   actorSwitched();setNoticeRejected(true);
   void api<Identity>('/auth/me').catch(recheckFailed);
  };
  window.addEventListener(ACTOR_MISMATCH_EVENT,mismatch);
  return()=>window.removeEventListener(ACTOR_MISMATCH_EVENT,mismatch);
 },[cache]);
 useEffect(()=>subscribeSession(signal=>{
  const action=classifySessionSignal(sessionRef.current.userKey,signal);
  if(action==='switched')actorSwitched();
  else if(action==='signed-out')clearSession();
 }),[cache]);
 useEffect(()=>{
  // Backstop for a sign-in this tab was not told about: re-check the cookie's owner when the tab is shown again.
  const shown=()=>{
   if(document.visibilityState!=='visible'||!sessionRef.current.userKey)return;
   // While the notice is up the user decides (Continue); the re-check only detects a sign-out or a lost connection.
   if(noticeRef.current){void api<Identity>('/auth/me').then(()=>setNoticeUnverified(false)).catch(recheckFailed);return}
   void api<Identity>('/auth/me').then(u=>accept(u)).catch(e=>{if(e instanceof ApiError&&e.status===401)clearSession()});
  };
  document.addEventListener('visibilitychange',shown);
  return()=>document.removeEventListener('visibilitychange',shown);
 },[cache]);
 useEffect(()=>{
  if(!user)return;
  const q=filter.trim().toLowerCase();
  const menu=visibleMenu(user);
  if(!q){
   setOpen(groupForPath(location.pathname));
   return;
  }
  setOpen(current=>{
   const matches=menu.filter(g=>g.name.toLowerCase().includes(q)||g.items.some(i=>i.label.toLowerCase().includes(q)));
   if(!matches.length)return current;
   if(matches.some(g=>g.name===current))return current;
   return matches[0]!.name;
  });
 },[filter,location.pathname,user]);

 if(loading||!sessionChecked)return <p className="workspace-loading">Loading workspace…</p>;
 if(host?.mode==='main'&&host.baseDomain)return <TenantFinder baseDomain={host.baseDomain}/>;
 if(!user){
  if(host?.mode==='tenant')return <Login onLogin={loggedIn} mode="tenant" tenantName={host.tenant?.name}/>;
  return <Login onLogin={loggedIn} mode="development"/>;
 }
 if(sessionNotice){
  // Rendering only the notice unmounts every screen, so no form typed under the previous user survives.
  return (
   <main className="login-wrap" role="alertdialog" aria-modal="true" aria-labelledby="session-changed-title" data-session-notice="">
    <section className="login-panel">
     <h1 id="session-changed-title">{SESSION_NOTICE_TITLE}</h1>
     <div role="alert">{sessionNoticeLines({rejected:noticeRejected,unverified:noticeUnverified}).map(line=><p key={line}>{line}</p>)}</div>
     <button type="button" className="primary" autoFocus disabled={continuing} onClick={continueAsCurrent}>{sessionNoticeAction({rejected:noticeRejected,unverified:noticeUnverified},continuing)}</button>
    </section>
   </main>
  );
 }

 const trail=crumb(location.pathname);
 const responsibility=user.permissions.includes('accounts.create')?'Company Super Admin':'User';
 const initial=(user.tenantName||'E').trim().charAt(0).toUpperCase();
 const groupIcon:Record<string,string>={Overview:'◉',Accounts:'▤','Financial Reports':'▧',Masters:'◫',Transactions:'◈',Reports:'▧',Administration:'▦','Platform Admin':'◆'};
 let previousCategory='ACCOUNTS';
 const defaultsAllowed=user.permissions.includes('defaults.read')&&user.modules.includes('accounts');
 const vouchersAllowed=user.permissions.includes('vouchers.read')&&user.modules.includes('accounts');
 const yarnAllowed=user.permissions.includes('grey.yarn.read')&&user.modules.includes('accounts');
 const qualityAllowed=user.permissions.includes('grey.quality.read')&&user.modules.includes('accounts');
 const locationAllowed=user.permissions.includes('grey.location.read')&&user.modules.includes('accounts');
 const contractAllowed=user.permissions.includes('grey.contract.read')&&user.modules.includes('accounts');
 const purchaseAllowed=user.permissions.includes('grey.purchase.read')&&user.modules.includes('accounts');
 const issueAllowed=user.permissions.includes('grey.issue.read')&&user.modules.includes('accounts');
 const receiptAllowed=user.permissions.includes('grey.receipt.read')&&user.modules.includes('accounts');
 const reconAllowed=user.permissions.includes('grey.recon.read')&&user.modules.includes('accounts');
 const costingAllowed=user.permissions.includes('grey.costing.read')&&user.modules.includes('accounts');
 const saleAllowed=user.permissions.includes('grey.sale.read')&&user.modules.includes('accounts');
 const millBillAllowed=user.permissions.includes('grey.millbill.read')&&user.modules.includes('accounts');
 const millPayAllowed=user.permissions.includes('grey.millpay.read')&&user.modules.includes('accounts');
 const millStmtAllowed=user.permissions.includes('grey.millstmt.read')&&user.modules.includes('accounts');
 const integrationAllowed=user.permissions.includes('accounts.integration.read')&&user.modules.includes('accounts');
 const menu=visibleMenu(user);
 const usersAllowed=routeAllowed(user,'/system/users');
 // Buttons only (backend doc 40 §4.2/§4.3): the server's capability answer per action; every route decides again.
 const rbac=user.authorizationModel==='RBAC';
 const ok=(key:string)=>cap(key).allowed;

 return (
  <div className="app-root">
   <div className="ebs-utility-bar no-print">
    <div className="ebs-utility-left"><span className="oracle-dot">{initial}</span><span>{user.tenantName}</span></div>
    <div className="ebs-utility-right">
     <button type="button" className="ebs-utility-link" onClick={()=>navigate('/accounts/chart')}>Chart of Accounts</button>
     {usersAllowed?<button type="button" className="ebs-utility-link" aria-label="Users directory" onClick={()=>navigate('/system/users')}>Users &amp; Roles</button>:null}
     <button type="button" className="ebs-utility-link" aria-label="Open reports" onClick={()=>vouchersAllowed&&navigate('/accounts/reports/journals')}>Reports</button>
     <button type="button" className="ebs-utility-link" onClick={()=>{void signOut()}}>Logout</button>
    </div>
   </div>
   <div className="app-body">
   <aside id="sidebar" className={navOpen?'open':undefined}>
    <div className="side-brand">
     <div className="side-mark">{initial}</div>
     <div>
      <div className="side-brand-name" title={user.tenantName}>{user.tenantName}</div>
      <div className="side-brand-plan">Enterprise plan</div>
     </div>
    </div>
    <div className="side-search">
     <div className="side-search-box"><span className="proto-icon" aria-hidden="true">⌕</span><input aria-label="Search menu" value={filter} onChange={e=>setFilter(e.target.value)} placeholder="Search menu"/></div>
    </div>
    <nav className="side-nav">
    {menu.map(g=>{
     const visible=g.items.filter(i=>i.label.toLowerCase().includes(filter.toLowerCase()));
     if(filter&&!visible.length&&!g.name.toLowerCase().includes(filter.toLowerCase()))return null;
     const caption=g.category&&g.category!==previousCategory?g.category:undefined;
     if(g.category)previousCategory=g.category;
     const expanded=open===g.name;
     return <div className={expanded?'side-group on':'side-group'} key={g.name}>
      {caption?<div className="sidebar-module-caption">{caption}</div>:null}
      <button type="button" className={expanded?'tree-head on':'tree-head'} aria-label={g.name} aria-expanded={expanded} onClick={()=>setOpen(open===g.name?'':g.name)}>
       <span className="proto-icon" aria-hidden="true">{groupIcon[g.name]||'•'}</span>
       <span className="tree-label">{g.name}</span>
       <span className="proto-tree-chevron" aria-hidden="true">›</span>
      </button>
      {expanded&&<div className="subtree">{(filter?visible:g.items).map(i=>{
       const active=!!i.to&&location.pathname===i.to;
       return <Link className="side-link" data-current={active?1:undefined} aria-current={active?'page':undefined} aria-label={i.label} key={i.label} to={i.to!} onClick={e=>{guardNavigate(e,i.to!);setNavOpen(false)}}><span>{i.label}</span></Link>;
      })}</div>}
     </div>;
    })}
    </nav>
    <div className="side-foot"><small>Signed in as</small><b>{user.displayName}</b></div>
   </aside>
   <main className="app-main workspace">
    <header className="app-header no-print">
     <button type="button" className="btn btn-icon nav-toggle" aria-label="Menu" aria-expanded={navOpen} onClick={()=>setNavOpen(v=>!v)}>☰</button>
     <div className="app-navigation-controls">
      <button type="button" className="btn btn-icon app-nav-icon" aria-label="Back" onClick={()=>navigate(-1)}>
       <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
      </button>
      <button type="button" className="btn btn-icon app-nav-icon" aria-label="Forward" onClick={()=>navigate(1)}>
       <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
      </button>
      <button type="button" className="btn btn-icon app-nav-icon" title="Dashboard" aria-label="Home" onClick={()=>navigate('/dashboard')}>⌂</button>
     </div>
     <div className="resp-block">
      <span className="eyebrow">Responsibility</span>
      <strong>{responsibility}</strong>
     </div>
     <select className="sel branch-select" aria-label="Branch" value={branch} onChange={e=>{
      if(!window.confirm('Switch branch? Unsaved form changes will be discarded.'))return;
      applyBranch(e.target.value);rememberBranch(user,e.target.value);cache.clear();
     }}>
      {user.branches.map(b=><option value={b.id} key={b.id}>{b.code} · {b.name}</option>)}
     </select>
     <div className="header-gap"/>
     {vouchersAllowed&&!location.pathname.startsWith('/accounts/vouchers')&&<button type="button" className="btn btn-orange" onClick={()=>navigate('/accounts/vouchers')}>＋ New voucher</button>}
     <button type="button" className="btn btn-icon" title="Notifications" aria-label="Notifications">●</button>
     <button type="button" className="btn btn-icon" title="Theme" aria-label="Theme" onClick={()=>{document.documentElement.dataset.theme=document.documentElement.dataset.theme==='dark'?'':'dark'}}>◐</button>
     <button type="button" className="btn btn-icon" title="Sign out" aria-label="Sign out" onClick={()=>{void signOut()}}>↪</button>
    </header>
    <div className="ebs-scroll" id="workspace">
    {!location.pathname.startsWith('/accounts')&&!location.pathname.startsWith('/system/')&&<div className="ebs-crumbbar grey-crumb"><div className="ebs-crumbs">{trail.section} &gt; {trail.page}</div></div>}
    <Suspense key={`${user.tenantId}|${user.id}|${branch}`} fallback={<p>Loading page…</p>}>
     <Routes>
      <Route path="/accounts/chart" element={<AccountsPage key={branch} branchId={branch} canCreate={user.permissions.includes('accounts.create')} canUpdate={user.permissions.includes('accounts.update')} canPartyRole={user.permissions.includes('grey.party.update')} canCashBank={user.permissions.includes('defaults.update')}/>}/>
      {vouchersAllowed?(
       <Route path="/accounts/vouchers" element={<VouchersPage key={branch} branchId={branch} canCreate={ok('voucherCreate')} canUpdate={ok('voucherUpdate')} canDelete={user.permissions.includes('vouchers.delete')} canPost={ok('voucherPost')} postReason={cap('voucherPost').reason} canReverse={ok('voucherReverse')} reverseReason={cap('voucherReverse').reason} openingBalancesAllowed={routeAllowed(user,'/accounts/opening-balances')} openingCanEdit={ok('openingBatchEntry')} openingCanApprove={ok('openingBatchPost')}/>}/>
      ):null}
      {integrationAllowed?(
       <Route path="/accounts/reports/posting-readiness" element={<PostingReadinessPage key={branch} branchId={branch}/>}/>
      ):null}
      {vouchersAllowed?(
       <Route path="/accounts/reports/:reportTab?" element={<FinancialReports key={branch} branchId={branch}/>}/>
      ):null}
      {defaultsAllowed?(
       <Route path="/grey/accounting-defaults" element={<AccountingDefaults key={branch} branchId={branch} canUpdate={user.permissions.includes('defaults.update')}/>}/>
      ):null}
      {yarnAllowed?(
       <Route path="/grey/yarn-specifications" element={<YarnSpecsPage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.yarn.create')} canUpdate={user.permissions.includes('grey.yarn.update')}/>}/>
      ):null}
      {qualityAllowed?(
       <Route path="/grey/quality-formula" element={<QualityFormulaPage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.quality.create')} canUpdate={user.permissions.includes('grey.quality.update')} canApprove={user.permissions.includes('grey.quality.approve')}/>}/>
      ):null}
      {locationAllowed?(
       <Route path="/grey/stock-locations" element={<StockLocationsPage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.location.create')} canUpdate={user.permissions.includes('grey.location.update')}/>}/>
      ):null}
      {contractAllowed?(
       <Route path="/grey/contracts" element={<ContractsPage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.contract.create')} canUpdate={user.permissions.includes('grey.contract.update')} canApprove={user.permissions.includes('grey.contract.approve')}/>}/>
      ):null}
      {purchaseAllowed?(
       <Route path="/grey/bag-purchases" element={<BagPurchasePage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.purchase.create')} canUpdate={user.permissions.includes('grey.purchase.update')} canConfirm={user.permissions.includes('grey.purchase.confirm')} canCancel={user.permissions.includes('grey.purchase.cancel')} canReview={user.permissions.includes('grey.purchase.review')}/>}/>
      ):null}
      {routeAllowed(user,'/grey/mill-yarn-returns')?(
       <Route path="/grey/mill-yarn-returns" element={<MillYarnReturnPage key={branch} branchId={branch}/>}/>
      ):null}
      {issueAllowed?(
       <Route path="/grey/mill-issues" element={<MillIssuePage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.issue.create')} canUpdate={user.permissions.includes('grey.issue.update')} canConfirm={user.permissions.includes('grey.issue.confirm')} canCancel={user.permissions.includes('grey.issue.cancel')}/>}/>
      ):null}
      {issueAllowed?(
       <Route path="/grey/opened-bags" element={<OpenBagPage key={branch} branchId={branch} canConfirm={user.permissions.includes('grey.issue.confirm')}/>}/>
      ):null}
      {receiptAllowed?(
       <Route path="/grey/fabric-receipts" element={<FabricReceiptPage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.receipt.create')} canUpdate={user.permissions.includes('grey.receipt.update')} canSubmit={user.permissions.includes('grey.receipt.submit')} canConfirm={user.permissions.includes('grey.receipt.confirm')} canCancel={user.permissions.includes('grey.receipt.cancel')}/>}/>
      ):null}
      {saleAllowed?(
       <Route path="/grey/fabric-sale-drafts" element={<SaleInvoicePage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.sale.create')} canUpdate={user.permissions.includes('grey.sale.update')} canReview={user.permissions.includes('grey.sale.review')} canReturnPost={ok('saleReturnConfirm')} canReadContracts={ok('customerContractRead')} canOverDeliver={ok('saleContractOverDelivery')}/>}/>
      ):null}
      {saleAllowed?(
       <Route path="/grey/sales-register" element={<SalesRegisterPage key={branch} branchId={branch}/>}/>
      ):null}
      {routeAllowed(user,'/grey/customer-contracts')?(
       <Route path="/grey/customer-contracts" element={<CustomerContractsPage key={branch} branchId={branch} can={{create:cap('customerContractCreate'),update:cap('customerContractUpdate'),approve:cap('customerContractApprove'),amend:cap('customerContractAmend'),close:cap('customerContractClose'),cancel:cap('customerContractCancel')}} user={{id:user.id,name:user.displayName}}/>}/>
      ):null}
      {routeAllowed(user,'/grey/reports/customer-contracts')?(
       <Route path="/grey/reports/customer-contracts" element={<CustomerContractStatusPage key={branch} branchId={branch}/>}/>
      ):null}
      {millBillAllowed?(
       <Route path="/grey/mill-bills" element={<MillBillPage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.millbill.create')} canUpdate={user.permissions.includes('grey.millbill.update')} canReview={user.permissions.includes('grey.millbill.review')}/>}/>
      ):null}
      {millBillAllowed?(
       <Route path="/grey/mill-charges-review" element={<MillChargesReviewPage key={branch} branchId={branch}/>}/>
      ):null}
      {millPayAllowed?(
       <Route path="/grey/mill-payment-requests" element={<MillPaymentPage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.millpay.create')} canUpdate={user.permissions.includes('grey.millpay.update')} canReview={user.permissions.includes('grey.millpay.review')}/>}/>
      ):null}
      {reconAllowed?(
       <Route path="/grey/mill-reconciliations" element={<MillReconciliationPage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.recon.create')} canUpdate={user.permissions.includes('grey.recon.update')} canSubmit={user.permissions.includes('grey.recon.submit')} canApprove={user.permissions.includes('grey.recon.approve')}/>}/>
      ):null}
      {contractAllowed?(
       <Route path="/grey/contract-position" element={<ContractPositionPage key={branch} branchId={branch}/>}/>
      ):null}
      {receiptAllowed?(
       <Route path="/grey/finished-fabric-stock" element={<FinishedFabricStockPage key={branch} branchId={branch} canReview={ok('saleLotsAuthorize')}/>}/>
      ):null}
      {costingAllowed?(
       <Route path="/grey/fabric-cost-analyses" element={<FabricCostAnalysisPage key={branch} branchId={branch} canCreate={user.permissions.includes('grey.costing.create')} canUpdate={user.permissions.includes('grey.costing.update')} canReview={user.permissions.includes('grey.costing.review')} {...(rbac?{canApproveReview:user.permissions.includes('grey.costing.review.approve'),canCertify:ok('costCertify')}:{})}/>}/>
      ):null}
      {millStmtAllowed?(
       <Route path="/grey/mill-statements" element={<MillStatementPage key={branch} branchId={branch}/>}/>
      ):null}
      {vouchersAllowed?(
       <Route path="/accounts/all-reports" element={<ComingSoon title="All Reports" trail="Financial Reports > All Reports"/>}/>
      ):null}
      {routeAllowed(user,'/grey/reports/mill-bags-balance')?(
       <Route path="/grey/reports/mill-bags-balance" element={<MillBagsBalancePage key={branch} branchId={branch}/>}/>
      ):null}
      {routeAllowed(user,'/grey/reports/fabric-receipt')?(
       <Route path="/grey/reports/fabric-receipt" element={<FabricReceiptReportPage key={branch} branchId={branch}/>}/>
      ):null}
      {routeAllowed(user,'/accounts/opening-balances')?(
       <Route path="/accounts/opening-balances" element={<OpeningBalancesPage key={branch} branchId={branch} canUpdate={ok('openingBatchEntry')} canPrepare={ok('openingItemPrepare')} canPost={ok('openingItemPost')} canApprove={ok('openingBatchPost')} canPrepareReversal={ok('openingItemPrepareReversal')} oneUser={rbac}/>}/>
      ):null}
      {routeAllowed(user,'/accounts/opening-stock-authorization')?(
       <Route path="/accounts/opening-stock-authorization" element={<OpeningStockAuthorizationPage key={branch} branchId={branch} canAuthorize={ok('openingStockAuthorize')} canSaleAuthorize={ok('openingStockSaleAuthorize')} reasonAuthorize={cap('openingStockAuthorize').reason} reasonSaleAuthorize={cap('openingStockSaleAuthorize').reason} simple={user.simpleMode===true}/>}/>
      ):null}
      {routeAllowed(user,'/accounts/tax-setup')?(
       <Route path="/accounts/tax-setup" element={<TaxSetupPage key={branch} branchId={branch} canRecord={user.permissions.includes('tax.code.record')} canApprove={ok('taxCodeApprove')} canRetire={ok('taxCodeRetire')} canEditProfile={user.permissions.includes('tax.profile.update')} canApproveProfile={ok('taxProfileApprove')} oneUser={rbac}/>}/>
      ):null}
      {routeAllowed(user,'/accounts/tax-register')?(
       <Route path="/accounts/tax-register" element={<TaxRegisterPage key={branch} branchId={branch}/>}/>
      ):null}
      {routeAllowed(user,'/accounts/advances')?(
       <Route path="/accounts/advances" element={<PartyAdvancesPage key={branch} branchId={branch} canRead={user.permissions.includes('party.advance.read')} canPrepare={user.permissions.includes('party.advance.prepare')} canPost={ok('partyAdvancePost')} canApply={user.permissions.includes('party.application.prepare')} canPostApplication={ok('partyApplicationPost')} canReverse={user.permissions.includes('defaults.update')} canConfirmReversal={ok('openingAdvanceReverseConfirm')} oneUser={rbac}/>}/>
      ):null}
      {routeAllowed(user,'/grey/purchase-returns')?(
       <Route path="/grey/purchase-returns" element={<PurchaseReturnPage key={branch} branchId={branch} canPrepare={user.permissions.includes('grey.purchase.update')} canPost={ok('purchaseReturnPost')} oneUser={rbac}/>}/>
      ):null}
      {routeAllowed(user,'/grey/reports/outstanding')?(
       <Route path="/grey/reports/outstanding" element={<OutstandingReportPage key={branch} branchId={branch}/>}/>
      ):null}
      {routeAllowed(user,'/grey/reports/inventory-valuation')?(
       <Route path="/grey/reports/inventory-valuation" element={<InventoryValuationPage key={branch} branchId={branch}/>}/>
      ):null}
      {routeAllowed(user,'/grey/reports/production-yield')?(
       <Route path="/grey/reports/production-yield" element={<ProductionYieldPage key={branch} branchId={branch}/>}/>
      ):null}
      {routeAllowed(user,'/system/financial-activation')?(
       <Route path="/system/financial-activation" element={<FinancialActivationPage key={branch} branchId={branch} permissions={user.permissions} oneUser={rbac}/>}/>
      ):null}
      {routeAllowed(user,'/system/branches')?(
       <Route path="/system/branches" element={<BranchesPage key={branch} branchId={branch} canCreate={user.permissions.includes('admin.branch.create')} canUpdate={user.permissions.includes('admin.branch.update')}/>}/>
      ):null}
      {usersAllowed?(
       <Route path="/system/users" element={<UsersPage key={branch} branchId={branch} currentUserId={user.id} multiBranch={user.multiBranchEnabled===true} defaultBranchId={branch} canCreate={user.permissions.includes('admin.user.create')} canUpdate={user.permissions.includes('admin.user.update')}/>}/>
      ):null}
      {routeAllowed(user,'/system/roles')?(
       <Route path="/system/roles" element={<RolesPage key={branch} branchId={branch} canCreate={user.permissions.includes('admin.role.create')} canUpdate={user.permissions.includes('admin.role.update')}/>}/>
      ):null}
      {/* Not gated by the menu: the user named in an owner-approved recovery may hold no permission yet. */}
      <Route path="/system/authorization" element={<AuthorizationPage key={branch} branchId={branch} canManage={user.permissions.includes('admin.authorization.manage')} canReport={user.permissions.includes('admin.authorization.report')} canReadRoles={user.permissions.includes('admin.role.read')}/>}/>
      {routeAllowed(user,'/system/settings')?(
       <Route path="/system/settings" element={<SettingsPage key={branch} branchId={branch} multiBranch={user.multiBranchEnabled===true} sessionBranches={user.branches} canUpdate={user.permissions.includes('admin.settings.update')}/>}/>
      ):null}
      {routeAllowed(user,'/system/fiscal-years')?(
       <Route path="/system/fiscal-years" element={<FiscalYearsPage key={branch} canCreate={user.permissions.includes('admin.fiscal.create')} canUpdate={user.permissions.includes('admin.fiscal.update')}/>}/>
      ):null}
      {routeAllowed(user,'/system/simple-mode')?(
       <Route path="/system/simple-mode" element={<SimpleModePage key={branch} canChange={user.permissions.includes('accounts.posting_control.update')}/>}/>
      ):null}
      {routeAllowed(user,'/system/companies')?(
       <Route path="/system/companies" element={<CompaniesPage/>}/>
      ):null}
      {user.isPlatformAdmin===true?(
       <Route path="/system/plans" element={<ComingSoon title="Plans & Billing" trail="System > Platform Admin > Plans & Billing"/>}/>
      ):null}
      <Route path="/dashboard" element={<DashboardPage key={branch} branchId={branch} user={user}/>}/>
      <Route path="*" element={<Navigate to="/dashboard" replace/>}/>
     </Routes>
    </Suspense>
    </div>
    <ShortcutStrip/>
   </main>
   </div>
  </div>
 );
}
